import { BillboardImage, BillboardImageRef, BillboardRenderer, Layer, View } from "@hscmap/stellar-globe";
import { LayerComponentBase } from "@/stellar-globe-components";
import { deepEqual } from "@/utils/deep";
import { V3, V4, clip } from "@/utils/math";
import { Component, Prop, Watch } from "vue-property-decorator";

export class MarkerLayer extends Layer {
  br!: BillboardRenderer

  constructor(private coord3: V3[], public color: V4, private imageData: ImageData) {
    super()
  }

  onAdd() {
    this.br = new BillboardRenderer(this.globe.gl)
    this.br.color = this.color
    this.rebuild()
  }

  onRemove() {
    this.br.release()
  }

  private rebuild() {
    if (this.coord3.length > 0) {
      const imageRefs: BillboardImageRef[] = []
      const marker: BillboardImage = {
        imageData: this.imageData,
        origin: [0, 0],
      }
      for (const coord of this.coord3) {
        imageRefs.push({
          imageID: 0,
          position: coord,
        })
      }
      this.br.buildArray([marker], imageRefs)
      this.globe.requestRefresh()
    }
  }

  setCoord3(coord3: V3[]) {
    this.coord3 = coord3
    this.rebuild()
  }

  setImageData(imageData: ImageData) {
    this.imageData = imageData
    this.rebuild()
  }

  render(view: View) {
    if (this.coord3.length > 0) {
      const minFovy = 3.e-4
      const maxFoxy = 3.e-2
      this.br.alpha =
        clip(+(view.camera.fovy - minFovy) * 2.e+3, 0, 1) *
        clip(-(view.camera.fovy - maxFoxy) * 4.e+1, 0, 1)
      this.br.render(view)
    }
  }
}

@Component
export default class MarkerLayerComponent extends LayerComponentBase<MarkerLayer> {
  @Prop({ required: true })
  coord3!: V3[]
  @Watch('coord3')
  onCoordChange(newCoord3: V3[], oldCoord3: V3[]) {
    if (!deepEqual(newCoord3, oldCoord3)) {
      this.layer.setCoord3(newCoord3)
    }
  }

  @Prop({ required: true })
  color!: V4
  @Watch('color')
  onColorChange() {
    this.layer.br.color = this.color
    this.globe.requestRefresh()
  }

  @Prop({ required: true })
  src!: string
  @Watch('src', { immediate: true })
  onSrcChange() {
    const img = new Image()
    img.onload = () => {
      if (this.layer.active()) {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')!
        canvas.width = img.width
        canvas.height = img.height
        ctx.drawImage(img, 0, 0)
        this.layer.setImageData(ctx.getImageData(0, 0, canvas.width, canvas.height))
      }
    }
    img.src = this.src
  }

  newLayer() {
    const imageData = new ImageData(1, 1)
    return new MarkerLayer(this.coord3, this.color, imageData)
  }
}
